import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminApp } from './admin/AdminApp';
import { Callback } from './auth/Callback';
import { signOut, startAxonLogin } from './auth/axon-auth';
import {
  clearToken,
  createAuthProvider,
  endSession,
  fetchCapabilities,
  fetchCurrentUser,
  ForbiddenError,
  restoreSession,
  type CurrentUser,
} from './auth/session';
import type { Theme } from './chat/theme';
import { ThemeToggle } from './Settings';
import './App.css';

type Status =
  | { kind: 'loading' }
  | { kind: 'ready'; user: CurrentUser }
  | { kind: 'forbidden'; message: string }
  | { kind: 'error'; message: string };

// Each element entry inlines its own runtime (Preact + chat client), and the
// components docs say to load one entry per page. Lazy wrappers split the
// bundles so / and /admin each fetch only the element they mount.
const AxonChat = lazy(() => import('./chat/AxonChat').then((m) => ({ default: m.AxonChat })));

// Two pages and the OAuth callback, told apart by pathname alone — no router.
// nginx (deploy/nginx.conf) and `vite dev` both fall back to index.html, so a
// deep link to /admin lands here too.
type Page = 'callback' | 'admin' | 'chat';

function pageFor(pathname: string): Page {
  const path = pathname.replace(/\/+$/, '');
  if (path === '/axon/oauth/callback') return 'callback';
  if (path === '/admin') return 'admin';
  return 'chat';
}

export function App() {
  const [page, setPage] = useState(() => pageFor(window.location.pathname));
  // In place, not a reload: the token lives in memory, and a reload would
  // drop the one the callback just received. Callback has already moved the
  // URL to /.
  const onSignedIn = useCallback(() => setPage('chat'), []);

  if (page === 'callback') return <Callback onSignedIn={onSignedIn} />;
  if (page === 'admin') return <AdminApp />;
  return <ChatApp />;
}

function ChatApp() {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [theme, setTheme] = useState<Theme>('dark');

  const load = useCallback(async () => {
    setStatus({ kind: 'loading' });
    try {
      // No session, in memory or in the refresh cookie → straight to the Axon
      // IdP (no interstitial sign-in page).
      if (!(await restoreSession())) {
        await startAxonLogin();
        return;
      }
      const user = await fetchCurrentUser();
      if (!user) {
        // 401: token missing/expired at the API → re-authenticate. The IdP uses
        // prompt=login, so a failed login stays on the IdP rather than bouncing
        // back here into a loop.
        clearToken();
        await startAxonLogin();
        return;
      }
      setStatus({ kind: 'ready', user });
    } catch (e) {
      if (e instanceof ForbiddenError) {
        // Authenticated but no chat access (403) — re-auth won't help, so don't
        // redirect. End the session — cookies included, or the refresh cookie
        // outlives it — and surface a terminal message.
        await endSession();
        setStatus({ kind: 'forbidden', message: e.message });
        return;
      }
      setStatus({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The Admin link is shown only to chat administrators. Asked after the chat
  // is ready so it never delays the mount, and any failure just leaves the
  // link hidden: it is a convenience, and /admin repeats the check anyway.
  const [isAdmin, setIsAdmin] = useState(false);
  const ready = status.kind === 'ready';
  useEffect(() => {
    if (!ready) {
      setIsAdmin(false);
      return;
    }
    const ctrl = new AbortController();
    fetchCapabilities(ctrl.signal)
      .then((c) => setIsAdmin(c?.is_chat_admin === true))
      .catch(() => {});
    return () => ctrl.abort();
  }, [ready]);

  // Set by Sign out. Clearing the token makes the element's next credential
  // request come back empty, which reports the session as ended; that must not
  // start a second sign-in racing the one Sign out starts.
  const signingOut = useRef(false);

  const onAuthRejected = useCallback(() => {
    // The element rejected auth mid-session (401) → re-authenticate.
    if (signingOut.current) return;
    clearToken();
    setStatus({ kind: 'loading' });
    void startAxonLogin();
  }, []);

  const auth = useMemo(() => createAuthProvider(onAuthRejected), [onAuthRejected]);

  return (
    <div className="app" data-theme={theme}>
      <header className="app__bar">
        <span className="app__brand">Axon Chat</span>
        <div className="app__settings">
          {isAdmin && (
            <a className="app__link" href="/admin">
              Admin
            </a>
          )}
          <ThemeToggle value={theme} onChange={setTheme} />
          {status.kind === 'ready' && (
            <button
              type="button"
              className="app__btn"
              onClick={() => {
                signingOut.current = true;
                setStatus({ kind: 'loading' });
                // Only the redirect to the IdP can fail here; endSession never
                // rejects, so the session is already gone.
                signOut().catch((e: unknown) => {
                  // Still on this page: a later rejection is real again.
                  signingOut.current = false;
                  setStatus({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
                });
              }}
            >
              Sign out
            </button>
          )}
        </div>
      </header>
      <main className="app__stage">
        {/* loading: render nothing — brief token-check / redirect gap */}
        {status.kind === 'forbidden' && (
          <div className="center">
            <p>{status.message}</p>
          </div>
        )}
        {status.kind === 'error' && (
          <div className="center">
            <p>Couldn&apos;t reach chat: {status.message}</p>
            <button type="button" className="app__btn" onClick={() => void load()}>
              Retry
            </button>
          </div>
        )}
        {status.kind === 'ready' && (
          <Suspense fallback={null}>
            <AxonChat user={status.user} auth={auth} theme={theme} />
          </Suspense>
        )}
      </main>
    </div>
  );
}
